import Phaser from "phaser";

export const GRID_COLS = 10;
export const GRID_ROWS = 12;
export const CELL_SIZE = 48;

/** 'P' = path cell, '.' = buildable, 'S' = spawn, 'E' = end (also path).
 *  12-row layout: top grass is 2 rows, mid-grass band (between road 1 and
 *  road 2) is 1 row. Serpentine path: spawn → right along row 2 → down col
 *  9 → left along row 4 → down col 0 → right along row 7 → down col 9 →
 *  bottom road → up col 0 → right to Throne at (col 4, row 9). */
const MAP_LAYOUT: string[] = [
  "S.........",   // row  0: spawn at top-left
  "P.........",   // row  1: col-0 descent through top grass
  "PPPPPPPPPP",   // row  2: first road (right)
  ".........P",   // row  3: mid grass + col-9 descent (1 row, was 2)
  "PPPPPPPPPP",   // row  4: second road (left)
  "P.........",   // row  5: col-0 descent
  "P.........",   // row  6: col-0 descent
  "PPPPPPPPPP",   // row  7: third road (right)
  ".........P",   // row  8: grass + col-9 descent
  "PPPPE....P",   // row  9: end row — Throne at col 4
  "P........P",   // row 10: col-0 and col-9 connectors
  "PPPPPPPPPP",   // row 11: bottom road
];

export type CellType = "path" | "build" | "spawn" | "end";

export interface Cell {
  col: number;
  row: number;
  x: number;
  y: number;
  type: CellType;
}

/**
 * Manages the grid, the enemy path (as a Phaser Curves.Path), and
 * provides cell-coord helpers for tower placement.
 */
export class GridPath {
  readonly cols = GRID_COLS;
  readonly rows = GRID_ROWS;
  readonly cellSize = CELL_SIZE;
  readonly width = GRID_COLS * CELL_SIZE;
  readonly height = GRID_ROWS * CELL_SIZE;
  readonly originX: number;
  readonly originY: number;

  readonly cells: Cell[][];
  readonly path: Phaser.Curves.Path;
  readonly pathLength: number;

  constructor(originX: number, originY: number) {
    this.originX = originX;
    this.originY = originY;

    if (MAP_LAYOUT.length !== GRID_ROWS) {
      throw new Error(`MAP_LAYOUT row count ${MAP_LAYOUT.length} != GRID_ROWS ${GRID_ROWS}`);
    }

    this.cells = [];
    for (let r = 0; r < GRID_ROWS; r++) {
      const row: Cell[] = [];
      const line = MAP_LAYOUT[r];
      if (line.length !== GRID_COLS) {
        throw new Error(`MAP_LAYOUT row ${r} length ${line.length} != GRID_COLS ${GRID_COLS}`);
      }
      for (let c = 0; c < GRID_COLS; c++) {
        const ch = line[c];
        const type: CellType =
          ch === "S" ? "spawn" : ch === "E" ? "end" : ch === "P" ? "path" : "build";
        row.push({
          col: c,
          row: r,
          x: originX + c * CELL_SIZE + CELL_SIZE / 2,
          y: originY + r * CELL_SIZE + CELL_SIZE / 2,
          type,
        });
      }
      this.cells.push(row);
    }

    // Build the path curve by walking from spawn to end through 'P' cells.
    const ordered = this.tracePath();
    if (ordered.length === 0) throw new Error("No path traced");

    this.path = new Phaser.Curves.Path(ordered[0].x, ordered[0].y);
    for (let i = 1; i < ordered.length; i++) {
      this.path.lineTo(ordered[i].x, ordered[i].y);
    }
    this.pathLength = this.path.getLength();
  }

  private tracePath(): Cell[] {
    const start = this.findCell("spawn");
    const end = this.findCell("end");
    if (!start || !end) throw new Error("Spawn or end cell missing");

    const visited = new Set<string>();
    const result: Cell[] = [];
    let current: Cell | null = start;
    let prev: Cell | null = null;

    while (current) {
      result.push(current);
      visited.add(`${current.col},${current.row}`);
      if (current.type === "end") break;

      const neighbors: Cell[] = this.neighbors(current).filter((n) => {
        if (visited.has(`${n.col},${n.row}`)) return false;
        return n.type === "path" || n.type === "end";
      });

      // Prefer continuing in the same direction first (avoids backtracks on ambiguous junctions).
      const next: Cell | null = this.preferStraight(prev, current, neighbors) ?? null;
      prev = current;
      current = next;
    }

    return result;
  }

  private preferStraight(prev: Cell | null, cur: Cell, options: Cell[]): Cell | undefined {
    if (options.length === 0) return undefined;
    if (!prev || options.length === 1) return options[0];
    const dx = cur.col - prev.col;
    const dy = cur.row - prev.row;
    const straight = options.find((o) => o.col - cur.col === dx && o.row - cur.row === dy);
    return straight ?? options[0];
  }

  private neighbors(c: Cell): Cell[] {
    const out: Cell[] = [];
    const offsets: Array<[number, number]> = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];
    for (const [dc, dr] of offsets) {
      const cell = this.getCell(c.col + dc, c.row + dr);
      if (cell) out.push(cell);
    }
    return out;
  }

  private findCell(type: CellType): Cell | undefined {
    for (const row of this.cells) {
      for (const c of row) if (c.type === type) return c;
    }
    return undefined;
  }

  getEndCell(): Cell {
    const end = this.findCell("end");
    if (!end) throw new Error("end cell missing");
    return end;
  }

  getSpawnCell(): Cell {
    const spawn = this.findCell("spawn");
    if (!spawn) throw new Error("spawn cell missing");
    return spawn;
  }

  getCell(col: number, row: number): Cell | undefined {
    if (col < 0 || col >= GRID_COLS || row < 0 || row >= GRID_ROWS) return undefined;
    return this.cells[row][col];
  }

  worldToCell(x: number, y: number): Cell | undefined {
    const col = Math.floor((x - this.originX) / CELL_SIZE);
    const row = Math.floor((y - this.originY) / CELL_SIZE);
    return this.getCell(col, row);
  }

  /** Draw the grid background — premium grass field with trees + cobblestone road. */
  draw(scene: Phaser.Scene): void {
    const rng = mulberry32(0xc0ffee);

    // 1) Grass base — rich dark green with per-cell shade variance + detail layer.
    const grass = scene.add.graphics();
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "build") continue;
        // 3-way shade split — mystical night battlefield (deep void blue-purple).
        const v = rng();
        const shade = v < 0.33 ? 0x141028 : v < 0.66 ? 0x181232 : 0x100c22;
        grass.fillStyle(shade, 1);
        grass.fillRect(cell.x - CELL_SIZE / 2, cell.y - CELL_SIZE / 2, CELL_SIZE, CELL_SIZE);
      }
    }
    // Grass micro-texture: darker blotches simulating soil shadow.
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "build") continue;
        const blotchCount = 3 + Math.floor(rng() * 4);
        for (let i = 0; i < blotchCount; i++) {
          const bx = cell.x - CELL_SIZE / 2 + 3 + rng() * (CELL_SIZE - 6);
          const by = cell.y - CELL_SIZE / 2 + 3 + rng() * (CELL_SIZE - 6);
          grass.fillStyle(0x0a0818, 0.4);
          grass.fillEllipse(bx, by, 5 + rng() * 7, 3 + rng() * 4);
        }
      }
    }
    // Grass tufts — slightly larger, two shades.
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "build") continue;
        const tuftCount = 3 + Math.floor(rng() * 4);
        for (let i = 0; i < tuftCount; i++) {
          const tx = cell.x - CELL_SIZE / 2 + 4 + rng() * (CELL_SIZE - 8);
          const ty = cell.y - CELL_SIZE / 2 + 4 + rng() * (CELL_SIZE - 8);
          // Glowing arcane motes scattered across the void floor.
          const tuftShade = rng() < 0.5 ? 0x3a2e7a : 0x2a4a8a;
          grass.fillStyle(tuftShade, 0.6);
          grass.fillCircle(tx, ty, 1.6 + rng() * 0.8);
        }
      }
    }
    // Tiny wildflowers — very sparse, adds life.
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "build" || rng() > 0.35) continue;
        const fx = cell.x - CELL_SIZE / 2 + 6 + rng() * (CELL_SIZE - 12);
        const fy = cell.y - CELL_SIZE / 2 + 6 + rng() * (CELL_SIZE - 12);
        // Glowing energy specks — cyan / violet / white star-dust.
        const flowerColor = rng() < 0.4 ? 0x7be3ff : rng() < 0.6 ? 0xc8a0ff : 0xffffff;
        grass.fillStyle(flowerColor, 0.9);
        grass.fillCircle(fx, fy, 0.9);
      }
    }

    // 2) Arcane energy lane — systematic 3×3 glowing-rune grid per cell.
    const road = scene.add.graphics();
    // Base fill (dark energy bed between glowing tiles).
    road.fillStyle(0x241838, 1);
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type === "path" || cell.type === "spawn" || cell.type === "end") {
          road.fillRect(cell.x - CELL_SIZE / 2, cell.y - CELL_SIZE / 2, CELL_SIZE, CELL_SIZE);
        }
      }
    }
    // Cobblestones — 3 cols × 3 rows of systematic pavers per cell.
    const COLS = 3, ROWS = 3;
    const GAP = 2;
    const stW = (CELL_SIZE - GAP * (COLS + 1)) / COLS;   // paver width  (~13px)
    const stH = (CELL_SIZE - GAP * (ROWS + 1)) / ROWS;   // paver height (~13px)
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "path" && cell.type !== "spawn" && cell.type !== "end") continue;
        const cx0 = cell.x - CELL_SIZE / 2;
        const cy0 = cell.y - CELL_SIZE / 2;
        for (let sc = 0; sc < COLS; sc++) {
          for (let sr = 0; sr < ROWS; sr++) {
            // Slight offset every other column for a brick-bond pattern.
            const offset = (sc % 2 === 1) ? stH * 0.5 : 0;
            const sx = cx0 + GAP + sc * (stW + GAP);
            const sy = cy0 + GAP + sr * (stH + GAP) + offset;
            // Clamp sy so stones stay within cell.
            if (sy < cy0 + 1 || sy + stH > cy0 + CELL_SIZE - 1) continue;
            // Glowing rune-tile shade — violet/indigo energy stones.
            const v = rng();
            const shade = v < 0.35 ? 0x4a2e7a : v < 0.7 ? 0x3a2566 : 0x2e1c54;
            road.fillStyle(shade, 1);
            road.fillRoundedRect(sx, sy, stW, stH, 2);
            // Top-left energy highlight (bright cyan-violet bevel).
            road.fillStyle(0x9a7aff, 0.5);
            road.fillRect(sx + 1, sy + 1, stW - 2, 1.5);
            road.fillRect(sx + 1, sy + 1, 1.5, stH - 2);
            // Bottom-right deep-shadow strip.
            road.fillStyle(0x140a28, 0.45);
            road.fillRect(sx + 1, sy + stH - 2.5, stW - 2, 1.5);
            road.fillRect(sx + stW - 2.5, sy + 1, 1.5, stH - 2);
          }
        }
      }
    }
    // Road edges — glowing energy curb where the lane meets the void floor.
    road.lineStyle(2, 0x9a7aff, 0.9);
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "path" && cell.type !== "spawn" && cell.type !== "end") continue;
        const sides: Array<[number, number, number, number]> = [
          [cell.x - CELL_SIZE / 2, cell.y - CELL_SIZE / 2, cell.x + CELL_SIZE / 2, cell.y - CELL_SIZE / 2],
          [cell.x + CELL_SIZE / 2, cell.y - CELL_SIZE / 2, cell.x + CELL_SIZE / 2, cell.y + CELL_SIZE / 2],
          [cell.x - CELL_SIZE / 2, cell.y + CELL_SIZE / 2, cell.x + CELL_SIZE / 2, cell.y + CELL_SIZE / 2],
          [cell.x - CELL_SIZE / 2, cell.y - CELL_SIZE / 2, cell.x - CELL_SIZE / 2, cell.y + CELL_SIZE / 2],
        ];
        const offsets: Array<[number, number]> = [[0, -1], [1, 0], [0, 1], [-1, 0]];
        sides.forEach((s, i) => {
          const nb = this.getCell(cell.col + offsets[i][0], cell.row + offsets[i][1]);
          if (!nb || nb.type === "build") {
            road.lineBetween(s[0], s[1], s[2], s[3]);
          }
        });
      }
    }
    // Inner shadow line just inside the curb for depth.
    road.lineStyle(1, 0x1e1a14, 0.6);
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "path" && cell.type !== "spawn" && cell.type !== "end") continue;
        const inset = 2;
        const sides2: Array<[number, number, number, number]> = [
          [cell.x - CELL_SIZE / 2 + inset, cell.y - CELL_SIZE / 2 + inset, cell.x + CELL_SIZE / 2 - inset, cell.y - CELL_SIZE / 2 + inset],
          [cell.x + CELL_SIZE / 2 - inset, cell.y - CELL_SIZE / 2 + inset, cell.x + CELL_SIZE / 2 - inset, cell.y + CELL_SIZE / 2 - inset],
          [cell.x - CELL_SIZE / 2 + inset, cell.y + CELL_SIZE / 2 - inset, cell.x + CELL_SIZE / 2 - inset, cell.y + CELL_SIZE / 2 - inset],
          [cell.x - CELL_SIZE / 2 + inset, cell.y - CELL_SIZE / 2 + inset, cell.x - CELL_SIZE / 2 + inset, cell.y + CELL_SIZE / 2 - inset],
        ];
        const offsets2: Array<[number, number]> = [[0, -1], [1, 0], [0, 1], [-1, 0]];
        sides2.forEach((s, i) => {
          const nb = this.getCell(cell.col + offsets2[i][0], cell.row + offsets2[i][1]);
          if (!nb || nb.type === "build") {
            road.lineBetween(s[0], s[1], s[2], s[3]);
          }
        });
      }
    }

    // 3) Trees on most buildable cells (sparser next to the path so combat reads clearly).
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.type !== "build") continue;
        const adjacentToPath = this.neighbors(cell).some((n) => n.type === "path" || n.type === "end" || n.type === "spawn");
        if (rng() > (adjacentToPath ? 0.3 : 0.82)) continue;
        const tx = cell.x + (rng() - 0.5) * 18;
        const ty = cell.y + (rng() - 0.5) * 18;
        drawTree(scene, tx, ty, rng);
      }
    }

    // 4) Spawn portal — swirling blue glow.
    const spawn = this.findCell("spawn")!;
    const portal = scene.add.circle(spawn.x, spawn.y, CELL_SIZE * 0.34, 0x4cc9f0, 0.55);
    portal.setStrokeStyle(2, 0xa6e3ff, 0.9);
    scene.tweens.add({
      targets: portal,
      scale: { from: 0.85, to: 1.05 },
      alpha: { from: 0.45, to: 0.7 },
      yoyo: true,
      repeat: -1,
      duration: 900,
      ease: "Sine.easeInOut",
    });
    scene.add.circle(spawn.x, spawn.y, CELL_SIZE * 0.18, 0x000020, 0.85);

    // 5) FLOWING ENERGY STREAM — bright motes of ki racing along the lane from
    //    spawn to throne, so the path visibly pulses with power (anime energy).
    const streamColors = [0x7be3ff, 0xc8a0ff, 0xffffff, 0x9a7aff];
    for (let i = 0; i < 14; i++) {
      const mote = scene.add.circle(spawn.x, spawn.y, 1.6 + Math.random() * 1.6,
        streamColors[i % streamColors.length], 0.9);
      mote.setBlendMode(Phaser.BlendModes.ADD);
      const glow = scene.add.circle(spawn.x, spawn.y, 4 + Math.random() * 2,
        streamColors[i % streamColors.length], 0.3);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      const startT = i / 14;
      const dur = 4200 + Math.random() * 1600;
      const driver = { t: startT };
      const path = this.path;
      const pt = new Phaser.Math.Vector2();
      scene.tweens.add({
        targets: driver,
        t: startT + 1,
        duration: dur,
        repeat: -1,
        ease: "Sine.easeInOut",
        onUpdate: () => {
          const tt = driver.t % 1;
          path.getPoint(tt, pt);
          const wobble = Math.sin(tt * 40 + i) * 3;
          mote.setPosition(pt.x + wobble, pt.y - 2);
          glow.setPosition(pt.x + wobble, pt.y - 2);
          // Fade in near spawn, fade out near throne so they "arrive" and vanish.
          const a = Math.sin(tt * Math.PI);
          mote.setAlpha(0.9 * a);
          glow.setAlpha(0.3 * a);
        },
      });
    }
  }
}

// ---------- Helpers ----------

function mulberry32(seed: number): () => number {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function drawTree(scene: Phaser.Scene, x: number, y: number, rng: () => number): void {
  const isPine = rng() < 0.65;
  const scale = 0.9 + rng() * 0.4;

  // Ground shadow
  scene.add.ellipse(x, y + 9 * scale, 16 * scale, 5 * scale, 0x000000, 0.38);

  // Trunk — with bark highlight
  scene.add.rectangle(x, y + 5 * scale, 3.5 * scale, 9 * scale, 0x3a2410).setStrokeStyle(1, 0x1a0e00, 1);
  scene.add.rectangle(x - 0.6 * scale, y + 4 * scale, 1 * scale, 7 * scale, 0x5a3820, 0.5);  // bark highlight

  if (isPine) {
    // Pine: 3 stacked tiers — dark green with a lighter highlight tip.
    const c1 = 0x2a5020;   // mid green
    const c2 = 0x1e4018;   // dark green bottom
    const c3 = 0x367030;   // lighter top tier
    // Bottom tier (widest + darkest)
    scene.add.triangle(x, y + 1 * scale, 0, 7 * scale, -10 * scale, 7 * scale, 10 * scale, 7 * scale, c2)
      .setStrokeStyle(1, 0x0a1e08, 1);
    // Mid tier
    scene.add.triangle(x, y - 5 * scale, 0, 7 * scale, -8 * scale, 7 * scale, 8 * scale, 7 * scale, c1)
      .setStrokeStyle(1, 0x0a1e08, 1);
    // Top tier (lightest)
    scene.add.triangle(x, y - 11 * scale, 0, 7 * scale, -5.5 * scale, 7 * scale, 5.5 * scale, 7 * scale, c3)
      .setStrokeStyle(1, 0x0a1e08, 1);
    // Snow/highlight tip dot
    scene.add.circle(x, y - 14 * scale, 1.2 * scale, 0xeafaea, 0.6);
  } else {
    // Deciduous: layered round canopy with highlight cluster.
    const cDark = 0x1e4a14;
    const cMid  = 0x2a5a1e;
    const cHi   = 0x3a7028;
    // Outer canopy blobs
    scene.add.circle(x,             y - 5 * scale, 8.5 * scale, cDark).setStrokeStyle(1, 0x0a1e08, 1);
    scene.add.circle(x - 5 * scale, y - 2 * scale, 6 * scale,   cDark).setStrokeStyle(1, 0x0a1e08, 1);
    scene.add.circle(x + 5 * scale, y - 2 * scale, 6 * scale,   cDark).setStrokeStyle(1, 0x0a1e08, 1);
    // Inner mid-tone layer
    scene.add.circle(x, y - 6 * scale, 6 * scale, cMid);
    // Highlight cluster (lighter top)
    scene.add.circle(x - 1.5 * scale, y - 8 * scale, 3.5 * scale, cHi, 0.75);
  }
}

